'use client';

import { featureFlagService } from '@/@feature-flags/services';
import {
    FEATURE_FLAGS_QUERY_CONFIG,
    FEATURE_FLAGS_QUERY_KEYS,
} from '@/@feature-flags/types/constants';
import type { FeatureFlagMap } from '@/@feature-flags/types/primitives';
import type { FeatureFlagsProviderProps } from '@/@feature-flags/types/props';
import { useSession } from '@/providers/enhanced-session';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface FeatureFlagsContextValue {
    flags: FeatureFlagMap;
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
    hasFlag: (key: string) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

export function FeatureFlagsProvider({ keys, children, initialFlags }: FeatureFlagsProviderProps) {
    const { data: session } = useSession();
    const queryClient = useQueryClient();

    const {
        data: accessResponse,
        isLoading,
        error,
    } = useQuery({
        queryKey: FEATURE_FLAGS_QUERY_KEYS.access(keys),
        queryFn: () => featureFlagService.checkAccess(keys),
        staleTime: FEATURE_FLAGS_QUERY_CONFIG.STALE_TIME,
        gcTime: FEATURE_FLAGS_QUERY_CONFIG.GC_TIME,
        enabled: !!session?.user?.id && keys.length > 0,
        // Seeds the query with what the root layout already resolved
        // server-side, so the real flag values are available from this
        // provider's very first render — the client-only query still runs
        // (staleTime 0 means it's treated as stale immediately) and quietly
        // corrects this if the backend value changed since, but the UI never
        // has to sit on the empty/false default while that happens.
        initialData: initialFlags ? { flags: initialFlags } : undefined,
    });

    const flags = useMemo<FeatureFlagMap>(() => accessResponse?.flags ?? {}, [accessResponse]);

    // Belt-and-suspenders against a hydration mismatch, now that `initialData`
    // above normally makes SSR and the client's first render agree on the
    // real flag values: if the root layout's own server-side flag fetch
    // fails or times out (see src/@feature-flags/server.ts), `initialFlags`
    // is `undefined`/`{}` and SSR renders with `flags = {}` while the
    // client's own query can still resolve `true` moments later, mid-
    // hydration — the exact non-deterministic mismatch this was written to
    // fix (confirmed live: `home-v2` observed flipping true/false across
    // renders within the same page load). Mirrors the same didSSR/useIsSSR
    // pattern react-aria uses for its own SSR-safe ids: stay at the SSR
    // default until strictly after mount, then allow the real value through
    // on an ordinary post-hydration re-render.
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
        setHasMounted(true);
    }, []);

    const hasFlag = useCallback(
        (key: string) => {
            // A key the server-side prefetch actually resolved (see
            // src/@feature-flags/server.ts) is safe to trust immediately,
            // without waiting on `hasMounted`: React serializes
            // `initialFlags` as a prop, so the client's first hydration
            // render reconstructs the exact same value SSR used rather than
            // recomputing it independently — there is no discrepancy left
            // to guard against for that key. Only keys the prefetch didn't
            // cover (a failed/timed-out fetch, or a key added after this
            // deploy) still wait on the mount gate below, since those are
            // the only ones that can still legitimately differ between SSR
            // and an early client render.
            if (initialFlags && key in initialFlags) {
                return flags[key] === true;
            }
            return hasMounted && flags[key] === true;
        },
        [hasMounted, flags, initialFlags],
    );

    const refetch = useCallback(() => {
        featureFlagService.clearCache();
        queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEYS.all });
    }, [queryClient]);

    const value = useMemo<FeatureFlagsContextValue>(
        () => ({ flags, isLoading, error: error as Error | null, refetch, hasFlag }),
        [flags, isLoading, error, refetch, hasFlag],
    );

    return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlagsContext(): FeatureFlagsContextValue {
    const context = useContext(FeatureFlagsContext);
    if (context === undefined) {
        throw new Error('useFeatureFlagsContext must be used within a FeatureFlagsProvider');
    }
    return context;
}
